import * as React from 'react';
import {useControlledState} from '@sail/react-aria';
import {PopoverConfig} from './PopoverConfig';
import {PopoverBottomSheet} from './Popover.bottomsheet';
import {DesktopPopover} from './Popover.desktop';
import {useShouldDisplayBottomSheet} from '../bottom-sheet/useShouldDisplayBottomSheet';
import {replaceDeprecatedProps} from './utils';

function isShowPropEnabled(
  value: boolean | Partial<{enabled: boolean}> | undefined,
): boolean {
  if (!value) {
    return false;
  }
  if (value === true) {
    return true;
  }
  if (typeof value === 'object') {
    return value.enabled !== false;
  }
  return false;
}

export const Popover = PopoverConfig.createView((propsIncludingDeprecated) => {
  const {
    open: controlledOpen,
    defaultOpen,
    onOpenChange: setControlledOpen,
    onClose,
    disableBottomSheet,
    ...props
  } = replaceDeprecatedProps(propsIncludingDeprecated);

  const shouldDisplayBottomSheet = useShouldDisplayBottomSheet('popover');

  const isHoverOnlyPopover =
    isShowPropEnabled(props.showOnHover) &&
    !isShowPropEnabled(props.showOnPress) &&
    !isShowPropEnabled(props.showOnFocus);

  const [open, setOpen] = useControlledState(
    // Here we default the controlled state to `true` if neither `open` nor `defaultOpen` are provided.
    // This provides backwards compatibility by continuing to call `onClose` events but not automatically
    // hiding the popover. This feels a little odd as you might reasonably expect the popover to be
    // uncontrolled by default, but it's the best we can do without breaking backwards compatibility. The
    // drawback is that you must explicitly provide the `defaultOpen` prop if you want the popover to be
    // uncontrolled.
    controlledOpen ?? (defaultOpen === undefined ? true : undefined),
    defaultOpen,
    setControlledOpen,
  );

  const onOpenChange = React.useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        // backwards compatibility for onClose
        onClose?.();
      }
    },
    [setOpen, onClose],
  );

  return shouldDisplayBottomSheet &&
    !disableBottomSheet &&
    !isHoverOnlyPopover ? (
    <PopoverBottomSheet
      {...props}
      open={open}
      onOpenChange={onOpenChange}
      popoverIsControlled={typeof controlledOpen !== 'undefined'}
    />
  ) : (
    <DesktopPopover
      {...props}
      open={open}
      onOpenChange={onOpenChange}
      popoverIsControlled={typeof controlledOpen !== 'undefined'}
    />
  );
});
